import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Img from "@insite/mobius/Img";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { css } from "styled-components";

const enum fields {
    region = "region",
    portalId = "portalId",
    formId = "formId",
    target = "target",
    css = "css",
}

interface Props extends WidgetProps {
    fields: {
        [fields.region]: string;
        [fields.portalId]: number;
        [fields.formId]: string;
        [fields.target]: string;
        [fields.css]: string;
    };
}

export interface Imgstyle {
    webExpBadge?: InjectableCss;
    webExpRequestConfirmationStepper?: InjectableCss;
}

export const hubspotStyle: Imgstyle = {
    webExpBadge: {
        css: css`
        float: right;
        margin-top: -105px;
        `
    },
    webExpRequestConfirmationStepper: {
        css: css`
        display: flex;
        gap: 16px;
        margin-top: 100px;

        .webExpStep {
            display: flex;
            gap: 16px
        }
        .webExpStepContent {
            display: flex;
            align-self: center;
            gap: 4px;
            img {
                display: flex;
                align-self: center;
            } 
            .webExpStepText{
                font-size: 12px;
                font-weight: 600;
                line-height: 16px;
                font-family: Poppins;
                display: flex;
                align-self: center;
                color: #266B40;
            }
        }
        .webExpStepLine {
            width: 72px;
            height: 1px;
            background: #266B40;
            display: flex;
            align-self: center;
            position: relative;
        }
        .webExpStepLine::after {
            content: "";
            position: absolute;
            right: -5px;
            top: 50%;
            transform: translateY(-50%);
            
            width: 0;
            height: 0;
            border-top: 4px solid transparent;
            border-bottom: 4px solid transparent;
            border-left: 6px solid #266B40;
        }
        .webExpStepCircle.text {
            font-size: 14px;
            font-weight: 600;
            height: 16px;
            width: 16px;
            background: #9E9D9E;
            color: white;
            display: flex;
            justify-content: center;
            align-items: center;
            border-radius: 100px;
            line-height: 16px;
        }

        @media (max-width: 768px) {
        .webExpStepContent {
            flex-direction: column;
        }
        .webExpStepContent img {
            align-self: self-start;
        }
        .webExpRequestConfirmationStepper .webExpStepLine {
            width: 32px;
        }
        .webExpRequestConfirmationStepper .webExpStep {
            gap: 6px
        }
}

   `
    },
};

const styles = hubspotStyle;

const CENHubspotScriptFormIframe: React.FunctionComponent<Props> = props => {
    const [isLoaded, setIsLoaded] = useState(false);
    const { productInfo, product } = React.useContext(ProductContext);
    const context = React.useContext(ProductContext);
    const inventoryInfo = productInfo?.inventory?.inventoryAvailabilityDtos;
    const availability = inventoryInfo && inventoryInfo[0]?.availability;
    const isCustomAvailabilityMsg = product?.properties?.isCustomAvailabilityMsg;
    const state = useAppSelector(
        (state) => state
    );

    const isPDP = getCurrentPage(state).type === "ProductDetailsPage";

    if (typeof window === "undefined") {
        return null;
    }

    function getDataLayer() {
        return (window as any).dataLayer;
    }
    const dataLayer = getDataLayer();

    useEffect(() => {
        const script = document.createElement("script");
        script.src = "https://js.hsforms.net/forms/v2.js";
        document.body.appendChild(script);
        script.addEventListener("load", () => {
            if ((window as any).hbspt !== undefined) {
                (window as any).hbspt.forms.create({
                    region: props.fields.region,
                    portalId: props.fields.portalId,
                    formId: props.fields.formId,
                    target: "#hubspot",
                    css: props.fields.css,
                    onFormReady() {
                        const iframe = document.querySelector(
                            "#hubspot iframe"
                        ) as HTMLIFrameElement | null;

                        if (iframe) {
                            iframe.addEventListener("load", () => {
                                setIsLoaded(true);
                            });
                        }
                    },
                    onFormSubmitted() {
                        setIsLoaded(false);
                        if (isPDP && availability?.messageType === 2 && isCustomAvailabilityMsg === "false") {
                            if (dataLayer) {
                                dataLayer.push({
                                    event: "getadeliverydateFormSubmit",
                                    formID: "get_a_delivery_date_for_my_location"
                                });
                            }
                        }
                        if (isPDP && availability?.messageType === 2 && isCustomAvailabilityMsg === "true") {
                            if (dataLayer) {
                                dataLayer.push({
                                    event: "canIgetThisFasterFormSubmit",
                                    formID: "can_i_get_this_faster"
                                });
                            }
                        }

                    },

                });
            }
        });

        return () => {
            document.body.removeChild(script);
        };
    }, [props.fields.region, props.fields.portalId, props.fields.formId, props.fields.css]);
    return <>
        <div id="hubspot"></div>
        {isLoaded && <div>
            <Img src="https://d1gr22c2amt84n.cloudfront.net/userfiles/images/centro-solves-badge.svg" width="72" height="72" {...styles.webExpBadge} />
            <StyledWrapper {...styles.webExpRequestConfirmationStepper}>
                <div className="webExpStep">
                    <div className="webExpStepContent">
                        <Img src="https://cdn.optimizely.com/img/21690680273/88a0e0bb123f4bf7b0fc530021d3f86b.jpg" width="16" height="16" />
                        <span className="webExpStepText">
                            You request
                        </span>
                    </div>
                    <div className="webExpStepLine">
                    </div>
                </div>
                <div className="webExpStep">
                    <div className="webExpStepContent">
                        <div className="webExpStepCircle text">
                            2
                        </div>
                        <span className="webExpStepText">
                            We verify stock
                        </span>
                    </div>
                    <div className="webExpStepLine">
                    </div>
                </div>
                <div className="webExpStep">
                    <div className="webExpStepContent">
                        <div className="webExpStepCircle text">
                            3
                        </div>
                        <span className="webExpStepText">
                            You get confirmation
                        </span>
                    </div>
                </div>

            </StyledWrapper>
        </div>}
    </>;
};

const widgetModule: WidgetModule = {
    component: CENHubspotScriptFormIframe,
    definition: {
        group: "Basic",
        icon: "Rtf",
        displayName: "CEN PDP Hubspot Form Iframe",
        fieldDefinitions: [
            {
                fieldType: "Translatable",
                name: fields.region,
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.portalId,
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.formId,
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.css,
                editorTemplate: "MultilineTextField",
                defaultValue: "",
            },
        ],
    },
};

export default widgetModule;
